import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ReadCvLogo, X } from "@phosphor-icons/react";
import { ANSignature } from "@answersnow/pkg-theme";
import useSignature from "hooks/useSignature";
import { LoadingButton } from "@mui/lab";

interface SignDocumentDialogProps {
  /** Document type display name (e.g. "Initial Treatment Plan") */
  documentTitle: string;
  /** Client name */
  clientName: string;
  /** Date the document was generated */
  dateGenerated: string;
  /** Attestation statements shown above the Sign & Verify action */
  attestations: string[];
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when signing is confirmed, receives the signature data */
  onSign: (signatureData: { dataUrl: string; buffer: Buffer }) => Promise<void> | void;
}

/**
 * Dialog for signing and verifying a generated document.
 * Uses ANSignature from pkg-theme for the Type/Draw signature pad.
 *
 * @param props
 * @param props.documentTitle - Document type display name
 * @param props.clientName - Client name
 * @param props.dateGenerated - Date the document was generated
 * @param props.attestations - Attestation statements for this document type
 * @param props.open - Whether the dialog is open
 * @param props.onClose - Callback to close the dialog
 * @param props.onSign - Callback when signing is confirmed
 */
const SignDocumentDialog: React.FC<SignDocumentDialogProps> = ({
  documentTitle,
  clientName,
  dateGenerated,
  attestations,
  open,
  onClose,
  onSign,
}) => {
  const { signatureRef, isValidSignature, handleSignatureChange, getSignatureData } =
    useSignature();
  const [isSigning, setIsSigning] = useState(false);

  const handleSign = async () => {
    const data = getSignatureData();
    if (!data) return;
    try {
      setIsSigning(true);
      await onSign(data);
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSigning ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2">
            Sign Document
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Signing will finalize and attach the document to {clientName}&apos;s official record.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small" disabled={isSigning}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack spacing={5} sx={{ mt: 7 }}>
          <Card variant="outlined" sx={{ borderRadius: 4, backgroundColor: "grey.50" }}>
            <CardHeader
              titleTypographyProps={{ variant: "body1", fontWeight: 500 }}
              subheaderTypographyProps={{ variant: "body2" }}
              avatar={
                <Avatar sx={{ bgcolor: "primary.background", width: 32, height: 32 }}>
                  <Box sx={{ color: "primary.main", display: "flex" }}>
                    <ReadCvLogo size={20} weight="duotone" />
                  </Box>
                </Avatar>
              }
              title={`${documentTitle} – ${clientName} – ${dateGenerated}`}
              subheader="Once finalized, this document will be visible on the client's account and available for external distribution."
            />
          </Card>

          <ANSignature
            width={500}
            height={150}
            options={{ resizeCanvas: true }}
            signaturePadRef={signatureRef}
            onSignatureChange={handleSignatureChange}
          />

          {attestations.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                By signing and finalizing this document, I attest that:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 5 }}>
                {attestations.map((item, index) => (
                  <Typography
                    key={index}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5 }}>
        <Button variant="contained" color="secondary" onClick={onClose} disabled={isSigning}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleSign}
          disabled={!isValidSignature}
          loading={isSigning}
        >
          {isSigning ? "Signing..." : "Sign & Verify"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SignDocumentDialog;
